import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, FaTint, FaCloudSun, FaRupeeSign, 
  FaBoxOpen, FaFileDownload, FaPhoneAlt, FaCheckCircle, 
  FaExternalLinkAlt, FaShieldAlt 
} from 'react-icons/fa';
import './Interventions.css';

const Interventions = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'hi';
  const isHindi = currentLang === 'hi';

  const [activeTab, setActiveTab] = useState('actions'); // 'actions' | 'schemes' | 'support'
  const [selectedActionDetail, setSelectedActionDetail] = useState(null);

  const actions = [
    {
      id: 1,
      priority: 1,
      badge: 'High Priority',
      badge_hi: 'उच्च प्राथमिकता',
      badge_color: 'red',
      title_en: 'Irrigate your crop within next 1-2 days',
      title_hi: 'सिंचाई करें',
      desc_en: 'Water stress is high at your crop stage. Apply light surface or drip irrigation to protect root zones.',
      desc_hi: 'अगले 1-2 दिनों में सिंचाई करना बहुत जरूरी है। फ्लावरिंग अवस्था में नमी की कमी से पैदावार प्रभावित हो सकती है।',
      icon: <FaTint />
    },
    {
      id: 2,
      priority: 2,
      badge: 'High Priority',
      badge_hi: 'उच्च प्राथमिकता',
      badge_color: 'red',
      title_en: 'Monitor upcoming weather before next activity',
      title_hi: 'मौसम पर नजर रखें',
      desc_en: 'Rainfall expected to be below normal for the next 5 days. Hold pesticide spray during high temperature peaks.',
      desc_hi: 'अगले 5 दिनों का मौसम अपडेट नियमित देखें। तेज धूप के समय कीटनाशक छिड़काव से बचें।',
      icon: <FaCloudSun />
    },
    {
      id: 3,
      priority: 3,
      badge: 'Medium Priority',
      badge_hi: 'मध्यम प्राथमिकता',
      badge_color: 'amber',
      title_en: 'Check better market / direct buyer options',
      title_hi: 'मार्केट विकल्प देखें',
      desc_en: 'Current local price is below MSP. Explore neighboring district mandis or list directly on FPO corporate buyer board.',
      desc_hi: 'नजदीकी मंडी और बेहतर कीमत के विकल्प देखें। वर्तमान भाव MSP से कम है, इसलिए FPO के माध्यम से सीधे खरीदार खोजें।',
      icon: <FaRupeeSign />
    },
    {
      id: 4,
      priority: 4,
      badge: 'Low Priority',
      badge_hi: 'कम प्राथमिकता',
      badge_color: 'green',
      title_en: 'Maintain proper storage if crop is harvested',
      title_hi: 'भंडारण पर ध्यान दें',
      desc_en: 'Maintain dry, ventilated shade storage to reduce spoilage decay curves under ambient heat.',
      desc_hi: 'पोस्ट-हार्वेस्ट स्टोरेज को सही बनाए रखें। हवादार और छायादार स्थान पर उपज रखकर नुकसान से बचें।',
      icon: <FaBoxOpen />
    }
  ];

  const schemes = [
    {
      id: 'pmfby',
      name: 'PM Fasal Bima Yojana (PMFBY)',
      name_hi: 'प्रधानमंत्री फसल बीमा योजना',
      desc_hi: 'मौसम की अनिश्चितता और सूखे से होने वाले नुकसान के लिए 100% बीमा सुरक्षा।',
      desc_en: 'Comprehensive crop insurance coverage against drought, dry spells, and climate shocks.',
      benefit: '₹ 50,000 / Hectare Max Claim',
      status: 'Pre-filled PDF Ready',
      status_hi: 'आवेदन पत्र तैयार है'
    },
    {
      id: 'pmkisan',
      name: 'PM-Kisan Samman Nidhi',
      name_hi: 'पीएम-किसान सम्मान निधि',
      desc_hi: 'छोटे व सीमांत किसानों के लिए प्रति वर्ष ₹6,000 की वित्तीय सहायता।',
      desc_en: 'Direct income support transfer of ₹6,000 per year for verified agricultural landholders.',
      benefit: '₹ 2,000 / Installment DBT',
      status: 'Auto-Matched',
      status_hi: 'पात्रता सत्यापित'
    },
    {
      id: 'soilcard',
      name: 'Soil Health Card Subsidy (Soil Rejuvenation)',
      name_hi: 'मृदा स्वास्थ्य सुधार अनुदान',
      desc_hi: 'मिट्टी में NPK और जैविक कार्बन सुधार हेतु 50% अनुदान पर बायो-फर्टिलाइजर।',
      desc_en: '50% subsidy on bio-stimulants and micronutrients to restore balanced NPK ratios.',
      benefit: '50% Subsidy on Nutrients',
      status: 'Eligible',
      status_hi: 'उपलब्ध'
    }
  ];

  return (
    <div className="fd-interventions-page">
      <div className="fd-interventions-container">
        {/* Top Back Nav */}
        <Link to="/risk-result" className="fd-interv-back-btn">
          <FaArrowLeft />
          <span>{isHindi ? 'वापस' : 'Back'}</span>
        </Link>

        {/* Action / Schemes Tabs */}
        <div className="fd-interv-tabs fd-card">
          <button 
            className={`fd-interv-tab ${activeTab === 'actions' ? 'active' : ''}`}
            onClick={() => setActiveTab('actions')}
          >
            {isHindi ? 'तुरंत क्या करें?' : 'Top Actions'}
          </button>
          <button 
            className={`fd-interv-tab ${activeTab === 'schemes' ? 'active' : ''}`}
            onClick={() => setActiveTab('schemes')}
          >
            {isHindi ? 'सरकारी योजनाएं' : 'Government Schemes'}
          </button>
          <button 
            className={`fd-interv-tab ${activeTab === 'support' ? 'active' : ''}`}
            onClick={() => setActiveTab('support')}
          >
            {isHindi ? 'अन्य सहायता' : 'Helpline & Support'}
          </button>
        </div>

        {/* Tab 1: Prioritized Action Items */}
        {activeTab === 'actions' && (
          <div className="fd-actions-tab-content">
            <div className="fd-tab-title-box">
              <h2>{isHindi ? 'तुरंत क्या करें? (प्राथमिकता के अनुसार)' : 'Top Actions (Priority Wise)'}</h2>
              <p>{isHindi ? 'डिस्ट्रेस कम करने के लिए समयबद्ध सिफारिशें' : 'Time-sensitive interventions to lower your DRI'}</p>
            </div>

            <div className="fd-action-cards-stack">
              {actions.map((act) => (
                <div key={act.id} className="fd-action-row-card fd-card">
                  <div className="fd-act-num-circle">{act.priority}</div>
                  <div className="fd-act-content-box">
                    <div className="fd-act-title-line">
                      <h3>{isHindi ? act.title_hi : act.title_en}</h3>
                      <span className={`fd-badge-${act.badge_color === 'red' ? 'high' : (act.badge_color === 'amber' ? 'mod' : 'low')}`}>
                        {isHindi ? act.badge_hi : act.badge}
                      </span>
                    </div>
                    <p className="fd-act-desc">{isHindi ? act.desc_hi : act.desc_en}</p>
                  </div>
                  <button 
                    className="fd-act-detail-btn"
                    onClick={() => setSelectedActionDetail(act.id === selectedActionDetail ? null : act.id)}
                  >
                    {isHindi ? 'विवरण देखें' : 'View Details'}
                  </button>
                </div>
              ))}
            </div>

            {/* Need Support Box */}
            <div className="fd-need-support-card fd-card">
              <div className="fd-ns-content">
                <h3>{isHindi ? 'सरकारी सहायता चाहिए?' : 'Need Government Support?'}</h3>
                <p>
                  {isHindi 
                    ? 'आपकी वर्तमान फसल स्थिति के आधार पर आप सरकारी योजनाओं के लिए पात्र हैं।'
                    : 'Based on your distress situation, you may be eligible for these emergency schemes & subsidies.'}
                </p>
                <button 
                  className="fd-btn-primary fd-view-schemes-btn"
                  onClick={() => setActiveTab('schemes')}
                >
                  {isHindi ? 'उपलब्ध योजनाएं देखें →' : 'View Relevant Schemes →'}
                </button>
              </div>
              <div className="fd-ns-plant-graphic">🌱</div>
            </div>
          </div>
        )}

        {/* Tab 2: Matched Government Schemes */}
        {activeTab === 'schemes' && (
          <div className="fd-schemes-tab-content">
            <div className="fd-tab-title-box">
              <h2>{isHindi ? 'पात्र सरकारी योजनाएं (Welfare Connector)' : 'Matched Government Schemes (AS-1)'}</h2>
              <p>{isHindi ? 'आपकी फसल व जोखिम प्रोफाइल के आधार पर स्वतः तैयार आवेदन' : 'Pre-filled emergency subsidy and insurance applications'}</p>
            </div>

            <div className="fd-schemes-list">
              {schemes.map((s) => (
                <div key={s.id} className="fd-scheme-item-card fd-card">
                  <div className="fd-scheme-top">
                    <div className="fd-scheme-hdr-left">
                      <FaShieldAlt className="fd-scheme-icon" />
                      <div>
                        <h3>{isHindi ? s.name_hi : s.name}</h3>
                        <span className="fd-scheme-benefit">{s.benefit}</span>
                      </div>
                    </div>
                    <span className="fd-badge-low">{isHindi ? s.status_hi : s.status}</span>
                  </div>

                  <p className="fd-scheme-desc">{isHindi ? s.desc_hi : s.desc_en}</p>

                  <div className="fd-scheme-bottom-action">
                    <button className="fd-btn-primary fd-pdf-claim-btn" onClick={() => window.print()}>
                      <FaFileDownload />
                      <span>{isHindi ? 'आवेदन पत्र डाउनलोड करें (PDF)' : 'Download Pre-filled Claim (PDF)'}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 3: Expert Helpline */}
        {activeTab === 'support' && (
          <div className="fd-support-tab-content">
            <div className="fd-tab-title-box">
              <h2>{isHindi ? 'किसान सहायता एवं विशेषज्ञ परामर्श' : 'Farmer Support & Advisory'}</h2>
              <p>{isHindi ? 'कृषि विज्ञान केंद्र और फील्ड विशेषज्ञों से सीधे जुड़ें' : 'Connect with KVK agronomists and local nodal officers'}</p>
            </div>

            <div className="fd-helpline-box fd-card">
              <div className="fd-hl-icon-wrap">
                <FaPhoneAlt />
              </div>
              <div className="fd-hl-info">
                <h3>{isHindi ? 'राष्ट्रीय किसान कॉल सेंटर (Kisan Call Center)' : 'Kisan Call Center Helpline'}</h3>
                <p>{isHindi ? 'टोल-फ्री नंबर पर निःशुल्क कृषि सलाह प्राप्त करें (सुबह 6 से रात 10 बजे तक)' : 'Toll-free 24/7 agronomist support'}</p>
                <a href="tel:18001801551" className="fd-call-phone-link">
                  📞 1800-180-1551
                </a>
              </div>
            </div>

            {/* Jain Irrigation Jalgaon Soil & Tissue Culture Agri Hub */}
            <div className="fd-helpline-box fd-card" style={{ borderLeft: '4px solid #10b981', marginTop: '16px' }}>
              <div className="fd-hl-icon-wrap" style={{ background: '#ecfdf5', color: '#059669' }}>
                <FaShieldAlt />
              </div>
              <div className="fd-hl-info">
                <h3>{isHindi ? 'जैन इरिगेशन सॉइल टेस्टिंग व टिशू कल्चर हब (जलगांव)' : 'Jain Agri R&D & Free Soil Testing Center (Jalgaon)'}</h3>
                <p>
                  {isHindi 
                    ? 'स्थान: एनएच-53, बांभोरी / जैन हिल्स, जलगांव - 425002। निःशुल्क NPK, pH, EC जांच और G9 टिशू कल्चर केला मार्गदर्शन उपलब्ध।' 
                    : 'Location: NH-53, Bambhori / Jain Hills, Jalgaon - 425002. Free NPK, pH, EC soil analysis & Grand Naine G9 Banana tissue culture advice.'}
                </p>
                <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
                  <a href="https://www.jains.com/" target="_blank" rel="noreferrer" className="fd-call-phone-link" style={{ background: '#059669', color: '#fff', padding: '6px 14px', borderRadius: '8px', textDecoration: 'none' }}>
                    🌐 Visit jains.com Portal
                  </a>
                  <a href="tel:18005993456" className="fd-call-phone-link">
                    📞 Toll Free: 1800-599-3456
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom Contact Expert Banner */}
        <div className="fd-expert-footer-banner">
          <div className="fd-ef-text">
            <h4>{isHindi ? 'अगर स्थिति गंभीर लगे तो विशेषज्ञ सलाह लें' : 'If condition feels critical, talk to an agronomist'}</h4>
            <p>{isHindi ? 'हमारे AI सहायक या कृषि विज्ञान केंद्र से तुरंत परामर्श लें' : 'Immediate voice guidance for field pest or moisture issues'}</p>
          </div>
          <a href="tel:18001801551" className="fd-btn-primary fd-expert-btn">
            <FaPhoneAlt />
            <span>{isHindi ? 'एक्सपर्ट से बात करें' : 'Talk to Expert'}</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Interventions;
